import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "@mui/material/Container";
import { DataLoaderSystemComponentContent } from "./DataLoaderSystem";
import {
  DataLoaderAdditionalAttribute,
  getAvailableAdditionalAttributeOptions,
  getDataSystemAdditionalAttributes,
  updateDataLoaderAdditionalAttributes,
} from "../../api/datasystems";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {
  Box,
  InputAdornment,
  ListSubheader,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import { isEmpty } from "lodash";

/**
 * List Config Props
 */
export interface DataLoaderAdditionalAttributesConfigProps {
  dataLoaderSystems: DataLoaderSystemComponentContent[];
  dataLoaderSystemIds?: number;
  existing?: boolean;
}

/**
 * Returns a new array of attributes sorted A-Z by name, ignoring case and accents
 */
const sortAttributesByName = (
  attributes: DataLoaderAdditionalAttribute[]
): DataLoaderAdditionalAttribute[] => {
  return [...attributes].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", undefined, {
      sensitivity: "base",
    })
  );
};

/**
 * A list selector
 * Establish a connection to the CRM and pull all available lists
 */
export const AdditionalAttributeSelection = (
  props: Readonly<{
    selectedAdditionalAttribute?: DataLoaderAdditionalAttribute;
    onAdd?(index: number): void;
    onRemove?(index: number): void;
    onChange(
      entity: string,
      name: string,
      configuration: string,
      index: number
    ): void;
    contactAdditionalAttribute: DataLoaderAdditionalAttribute[];
    accountAdditionalAttribute: DataLoaderAdditionalAttribute[];
    index: number;
  }>
) => {
  const {
    selectedAdditionalAttribute,
    onAdd,
    onRemove,
    onChange,
    contactAdditionalAttribute,
    accountAdditionalAttribute,
    index,
  } = props;

  const [selectedEntity, setSelectedEntity] = useState<string>(
    selectedAdditionalAttribute.entity
  );
  const [selectedConfiguration, setSelectedConfiguration] = useState<string>(
    selectedAdditionalAttribute.configuration
  );
  const [additionalAttributes, setAdditionalAttributes] = useState<
    DataLoaderAdditionalAttribute[]
  >([]);
  const [attributeSearch, setAttributeSearch] = useState("");

  const visibleAttributes = useMemo(() => {
    const searchTerm = attributeSearch.trim().toLowerCase();
    return additionalAttributes.filter((additionalAttribute) =>
      (additionalAttribute.name ?? "").toLowerCase().includes(searchTerm)
    );
  }, [additionalAttributes, attributeSearch]);

  const hiddenSelectedAttribute = selectedAdditionalAttribute.configuration
    ? additionalAttributes.find(
        (additionalAttribute) =>
          additionalAttribute.configuration ===
            selectedAdditionalAttribute.configuration &&
          !visibleAttributes.includes(additionalAttribute)
      )
    : undefined;

  useEffect(() => {
    if (
      selectedEntity == "" &&
      selectedAdditionalAttribute.entity == "CONTACT"
    ) {
      setAdditionalAttributes(contactAdditionalAttribute);
    } else if (
      selectedEntity == "" &&
      selectedAdditionalAttribute.entity == "ACCOUNT"
    ) {
      setAdditionalAttributes(accountAdditionalAttribute);
    }
  }, [selectedAdditionalAttribute]);

  useEffect(() => {
    if (selectedEntity == "CONTACT") {
      setAdditionalAttributes(contactAdditionalAttribute);
    } else if (selectedEntity == "ACCOUNT") {
      setAdditionalAttributes(accountAdditionalAttribute);
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (selectedEntity != selectedAdditionalAttribute.entity) {
      onChange(selectedEntity, "", "", index);
    }
  }, [selectedEntity]);

  useEffect(() => {
    let selectedNames = additionalAttributes.filter((item) => {
      return item.configuration == selectedConfiguration;
    });
    if (selectedNames.length == 1) {
      let selectedName = selectedNames[0].name;
      onChange(selectedEntity, selectedName, selectedConfiguration, index);
    }
  }, [selectedConfiguration]);

  return (
    <Box sx={{ display: "flex", flexDirection: "row" }}>
      <Select
        variant={"standard"}
        data-testid={"selectentity"}
        value={selectedAdditionalAttribute.entity}
        onChange={(event) => {
          setSelectedEntity(event.target.value);
        }}
      >
        <MenuItem key="CONTACT" value="CONTACT">
          Contact
        </MenuItem>
        <MenuItem key="ACCOUNT" value="ACCOUNT">
          Account
        </MenuItem>
      </Select>

      <Select
        variant={"standard"}
        data-testid={"select"}
        style={{ flex: 1, maxWidth: "80%" }}
        value={selectedAdditionalAttribute.configuration}
        onChange={(event) => {
          setSelectedConfiguration(event.target.value);
        }}
        onClose={() => setAttributeSearch("")}
        MenuProps={{
          autoFocus: false,
          PaperProps: { style: { maxHeight: 400 } },
        }}
      >
        <ListSubheader>
          <TextField
            fullWidth
            size="small"
            placeholder="Search attributes..."
            value={attributeSearch}
            variant={"standard"}
            autoFocus
            inputProps={{ "data-testid": "attribute_search" }}
            onChange={(event) => setAttributeSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Escape") {
                event.stopPropagation();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </ListSubheader>
        {visibleAttributes.map((additionalAttribute) => {
          return (
            <MenuItem
              key={additionalAttribute.configuration}
              value={additionalAttribute.configuration}
            >
              {additionalAttribute.name}
            </MenuItem>
          );
        })}
        {hiddenSelectedAttribute && (
          <MenuItem
            key={hiddenSelectedAttribute.configuration}
            value={hiddenSelectedAttribute.configuration}
            sx={{ display: "none" }}
          >
            {hiddenSelectedAttribute.name}
          </MenuItem>
        )}
        {visibleAttributes.length === 0 && attributeSearch.trim() !== "" && (
          <MenuItem disabled>No matching attributes</MenuItem>
        )}
      </Select>
      <div style={{ display: "flex" }}>
        <IconButton
          data-testid={"remove_condition_btn"}
          aria-label="remove_condition"
          color="primary"
          disabled={!onRemove}
          onClick={(_event) => onRemove(index)}
          size="small"
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
        <IconButton
          data-testid={"add_condition_btn"}
          aria-label="add_condition"
          color="primary"
          size="small"
          disabled={!onAdd}
          onClick={(_event) => onAdd(index)}
        >
          <AddCircleOutlineOutlinedIcon />
        </IconButton>
      </div>
    </Box>
  );
};

/**
 * Renders the additional attribute selector and handles all save/ update operations
 *
 */
export function DataLoaderAdditionalAttributesConfig(
  props: Readonly<DataLoaderAdditionalAttributesConfigProps>
) {
  const { enqueueSnackbar } = useSnackbar();
  const { dataLoaderSystems, dataLoaderSystemIds, existing } = props;

  const [selectedAdditionalAttributes, setSelectedAdditionalAttributes] =
    useState<DataLoaderAdditionalAttribute[]>([]);

  const [dataLoaderSystem, setDataLoaderSystem] =
    useState<DataLoaderSystemComponentContent>({
      integrations: undefined,
      list_ids: [],
      name: "",
      options: undefined,
      type: "",
    });
  const [contactAdditionalAttributeOptions, setContactAdditionalAttribute] =
    useState<DataLoaderAdditionalAttribute[]>([]);
  const [accountAdditionalAttributeOptions, setAccountAdditionalAttribute] =
    useState<DataLoaderAdditionalAttribute[]>([]);
  const lastRequestedOptionsKey = useRef<string | undefined>(undefined);
  const lastLoadedAdditionalAttributesSystemId = useRef<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (dataLoaderSystems && dataLoaderSystems.length > 0) {
      setDataLoaderSystem(dataLoaderSystems[0]);
    }
  }, [dataLoaderSystems]);

  useEffect(() => {
    const optionsAreReady = existing
      ? dataLoaderSystem.optionsLoadedFromBackend === true
      : !isEmpty(dataLoaderSystem.options);
    if (!dataLoaderSystem.type || !optionsAreReady) {
      setContactAdditionalAttribute([]);
      setAccountAdditionalAttribute([]);
      lastRequestedOptionsKey.current = undefined;
      return;
    }

    const requestKey = JSON.stringify({
      type: dataLoaderSystem.type,
      options: dataLoaderSystem.options,
      dataLoaderSystemIds,
    });
    if (lastRequestedOptionsKey.current === requestKey) {
      return;
    }

    lastRequestedOptionsKey.current = requestKey;
    getAvailableAdditionalAttributeOptions(
      dataLoaderSystem.type,
      dataLoaderSystem.options
    )
      .then((response) => {
        setContactAdditionalAttribute(
          sortAttributesByName(response.CONTACTS ?? [])
        );
        setAccountAdditionalAttribute(
          sortAttributesByName(response.ACCOUNTS ?? [])
        );
      })
      .catch((err) => {
        lastRequestedOptionsKey.current = undefined;
        console.log(err);
      });
  }, [
    dataLoaderSystem.type,
    dataLoaderSystem.options,
    dataLoaderSystemIds,
    existing,
  ]);

  useEffect(() => {
    const optionsAreReady = dataLoaderSystem.optionsLoadedFromBackend === true;
    if (!dataLoaderSystem.type) {
      return;
    }

    if (!existing || !dataLoaderSystemIds) {
      setSelectedAdditionalAttributes([
        { entity: "", name: "", configuration: "" },
      ]);
      lastLoadedAdditionalAttributesSystemId.current = undefined;
      return;
    }

    if (
      !optionsAreReady ||
      contactAdditionalAttributeOptions.length === 0 ||
      accountAdditionalAttributeOptions.length === 0
    ) {
      return;
    }

    if (
      lastLoadedAdditionalAttributesSystemId.current === dataLoaderSystemIds
    ) {
      return;
    }

    lastLoadedAdditionalAttributesSystemId.current = dataLoaderSystemIds;
    getDataSystemAdditionalAttributes(dataLoaderSystemIds)
      .then((response) => {
        if (response.length) {
          setSelectedAdditionalAttributes(response);
        } else {
          setSelectedAdditionalAttributes([
            { entity: "", name: "", configuration: "" },
          ]);
        }
      })
      .catch((err) => {
        lastLoadedAdditionalAttributesSystemId.current = undefined;
        console.log(err);
      });
  }, [
    contactAdditionalAttributeOptions,
    accountAdditionalAttributeOptions,
    dataLoaderSystem.type,
    dataLoaderSystem.options,
    dataLoaderSystemIds,
    existing,
  ]);

  const onAdd = (index: number) => {
    setSelectedAdditionalAttributes([
      ...selectedAdditionalAttributes.slice(0, index + 1),
      { entity: "", name: "", configuration: "" },
      ...selectedAdditionalAttributes.slice(index + 1),
    ]);
  };

  const onRemove = (index: number) => {
    if (selectedAdditionalAttributes.length === 1) {
      setSelectedAdditionalAttributes([]);
    } else {
      setSelectedAdditionalAttributes([
        ...selectedAdditionalAttributes.slice(0, index),
        ...selectedAdditionalAttributes.slice(index + 1),
      ]);
    }
  };

  const onChange = (
    entity: string,
    name: string,
    configuration: string,
    index: number
  ) => {
    setSelectedAdditionalAttributes([
      ...selectedAdditionalAttributes.slice(0, index),
      { entity: entity, name: name, configuration: configuration },
      ...selectedAdditionalAttributes.slice(index + 1),
    ]);
  };
  return (
    <>
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "10px 5px 5px 5px",
          marginBottom: "20px",
        }}
      >
        {selectedAdditionalAttributes.length > 0 ? (
          selectedAdditionalAttributes.map(
            (
              selectedAdditionalAttribute: DataLoaderAdditionalAttribute,
              index
            ) => {
              return (
                // Rows have no id of their own and every callback addresses
                // them by position, so position is the identity.
                <AdditionalAttributeSelection
                  key={index}
                  selectedAdditionalAttribute={selectedAdditionalAttribute}
                  onAdd={onAdd}
                  onRemove={onRemove}
                  onChange={onChange}
                  contactAdditionalAttribute={contactAdditionalAttributeOptions}
                  accountAdditionalAttribute={accountAdditionalAttributeOptions}
                  index={index}
                />
              );
            }
          )
        ) : (
          <Typography variant={"body2"}>
            No Additional Attributes Configuration available for this CRM.
          </Typography>
        )}
      </Container>
      {existing && contactAdditionalAttributeOptions.length > 0 && (
        <Button
          onClick={() => {
            updateDataLoaderAdditionalAttributes({
              data_loader_system_id: dataLoaderSystemIds,
              additional_attributes: selectedAdditionalAttributes,
            })
              .then(() => {
                enqueueSnackbar("Additional attributes saved successfully!", {
                  variant: "success",
                });
              })
              .catch(() => {
                enqueueSnackbar("Unable to save additional attributes.", {
                  variant: "error",
                });
              });
          }}
        >
          Apply
        </Button>
      )}
    </>
  );
}
